/**
 * ticket-inapp.stencil-fit-text — the value-slot counterpart
 * to `<tkt-fitted-wordmark>` (ticket-inapp.stencil-wordmark.ts): the stencil
 * card's four value slots (date, venue, ticket type, ticket number) are
 * operator/buyer-controlled strings with no length ceiling this repo
 * controls, so each one must fit its reserved column on ONE line for any
 * realistic value, and must NEVER clip or truncate for a pathological one —
 * shrinking, never `line-clamp`, never `text-overflow: ellipsis`.
 *
 * THE ALGORITHM, IN ONE PASS: measure the text's own natural (`nowrap`)
 * rendered width against the box `<tkt-fit-text>` fills.
 *   1. It already fits — no correction, natural size.
 *   2. It doesn't fit, but shrinking down to `minScale` would make it fit on
 *      one line — scale by exactly the ratio needed (between `minScale` and
 *      1), still one line.
 *   3. Even `minScale` isn't enough on one line (the legibility floor) —
 *      clamp to `minScale` and let it wrap: `white-space: normal` plus a
 *      `max-width` equal to the box's own width DIVIDED by `minScale`, so
 *      the wrapped (now possibly multi-line) block, once scaled down by
 *      `minScale`, lands back inside the box exactly. This is the ONLY path
 *      that produces more than one line, and it exists solely so an extreme
 *      input degrades to wrapped-but-whole text instead of illegible
 *      microscopic type or a clipped/truncated string — a realistic value
 *      never reaches it.
 *
 * THE CALLER RESERVES THE SECOND LINE'S HEIGHT UNCONDITIONALLY —
 * ticket-inapp.stencil.template.ts sets a fixed `min-height` (two line-heights)
 * on every value slot's own wrapper regardless of whether THIS particular
 * render's content needs it, so the QR block and footer never shift position
 * depending on how long any one value happens to be — the same overlap-bug
 * shape a content-dependent height would reintroduce, just at the row level
 * instead of the wordmark's.
 */
import { html, LitElement, type TemplateResult } from "lit";
import {
	ensureTicketCardFonts,
	TICKET_CARD_TEXT_FAMILY,
	ticketCardFontSet,
} from "./ticket-inapp.fonts.ts";

const FIT_TEXT_TAG = "tkt-fit-text";

class TktFitText extends LitElement {
	static override properties = {
		text: { type: String },
		fontSizePx: { type: Number, attribute: "font-size-px" },
		minScale: { type: Number, attribute: "min-scale" },
	};

	declare text: string;
	declare fontSizePx: number;
	declare minScale: number;

	constructor() {
		super();
		this.text = "";
		this.fontSizePx = 14;
		// 0.62: comfortably legible at this card's own 384px design width —
		// the floor below which a value wraps (see this file's header)
		// rather than keeps shrinking toward illegibility.
		this.minScale = 0.62;
	}

	override createRenderRoot(): this {
		return this;
	}

	override connectedCallback(): void {
		super.connectedCallback();
		this.style.display = "block";
		this.style.width = "100%";
		// This host renders no text of its own, only the span above does — but
		// as a block box wrapping inline content, it still generates its own
		// "strut" (an invisible zero-width inline box using THIS element's own
		// inherited font-size/line-height, not the span's explicit one) that
		// adds to the line box's total height alongside the span's own real
		// content. Caught by a real render, not assumed: the same defect this
		// file's sibling `<tkt-fitted-wordmark>` (ticket-inapp.stencil-wordmark.ts)
		// already carries this identical fix for — here it showed up as this
		// host's own rendered height exceeding the caller's `min-height`
		// reservation (`VALUE_MIN_HEIGHT_PX`, sized off the span's own
		// explicit `line-height: 1.2`) by a few pixels on a wrapped (2-line)
		// value, pushing the QR block and footer down the card.
		// `line-height: 0` zeroes this host's own strut so its line box height
		// comes from the span's real content alone.
		this.style.lineHeight = "0";
		// The text face this value is measured and drawn in, registered by the
		// card rather than by the host — ticket-inapp.fonts.ts. Idempotent.
		ensureTicketCardFonts();
	}

	override render(): TemplateResult {
		// line-height: 1.2 is explicit, not the browser/font default `normal`
		// it would otherwise fall back to — caught by a real render, not
		// assumed: the caller's own `min-height` reservation around this
		// element (ticket-inapp.stencil.template.ts's `VALUE_MIN_HEIGHT_PX`,
		// `fontSizePx * 1.2 * 2`) is arithmetic against THIS exact ratio, so a
		// wrapped (2-line, the legibility-floor fallback's own path) value's
		// REAL rendered height has to equal what that arithmetic assumed, not
		// whatever a particular font's own metrics happen to compute for
		// `normal` (measured ~20px/line here against the ~15.6px/line the
		// caller's own budget assumed, for the identical font-size) — a gap
		// wide enough that the wrapped block's own real height overflowed the
		// reservation and pushed every section below it down the card.
		return html`<span
      class="tkt-fit-run"
      style="font-size: ${this.fontSizePx}px; line-height: 1.2; transform-origin: left center;"
      >${this.text}</span
    >`;
	}

	#applyFit(): void {
		// data-fit-gen increments on every #applyFit call regardless of
		// outcome — see TktFittedWordmark's own identical field
		// (ticket-inapp.stencil-wordmark.ts) for why a poll needs this rather
		// than the transform itself: a value that already fits on one line
		// legitimately leaves no transform set at all.
		this.dataset.fitGen = String((Number(this.dataset.fitGen) || 0) + 1);
		const span = this.firstElementChild as HTMLElement | null;
		if (!span) return;
		span.style.transform = "none";
		span.style.whiteSpace = "nowrap";
		span.style.maxWidth = "none";
		span.style.overflowWrap = "normal";
		const naturalWidth = span.getBoundingClientRect().width;
		const targetWidth = this.getBoundingClientRect().width;
		if (naturalWidth <= 0 || targetWidth <= 0 || naturalWidth <= targetWidth) {
			return;
		}
		const directRatio = targetWidth / naturalWidth;
		if (directRatio >= this.minScale) {
			span.style.transform = `scale(${directRatio})`;
			return;
		}
		span.style.whiteSpace = "normal";
		span.style.overflowWrap = "anywhere";
		span.style.maxWidth = `${targetWidth / this.minScale}px`;
		span.style.transform = `scale(${this.minScale})`;
	}

	// updated() — Lit's own unconditionally-firing post-render hook — is the
	// correction's real trigger, not the getUpdateComplete override below:
	// see ticket-inapp.stencil-wordmark.ts's own TktFittedWordmark for the
	// full reasoning (caught by a real render there, not assumed) — nothing
	// in a live host page (the studio, the storefront, the catalog, the
	// picker) ever reads this element's `updateComplete`, so a correction
	// that only ran there never ran at all past this element's first mount.
	override updated(changed: Map<PropertyKey, unknown>): void {
		super.updated(changed);
		this.#applyFit();
		void this.#applyFitOnceFontLoads();
	}

	// The awaited half — belt and braces alongside updated()'s own immediate
	// correction: this element's FIRST ever render can still land before
	// Poppins has actually finished loading (a real network/file fetch in
	// every live-browser host), so updated()'s own immediate #applyFit
	// measures the fallback font's narrower or wider natural width; this
	// re-applies once the real face is confirmed loaded.
	async #applyFitOnceFontLoads(): Promise<void> {
		try {
			// Null in a host with no Font Loading API — see
			// ticket-inapp.fonts.ts's own `ticketCardFontSet`.
			await ticketCardFontSet()?.load(
				`400 ${this.fontSizePx}px "${TICKET_CARD_TEXT_FAMILY}"`,
			);
		} catch {
			// A load() rejection still lets the fit stand at whatever
			// updated() already applied against the fallback font —
			// wrong-looking, never clipped.
		}
		this.#applyFit();
	}

	override async getUpdateComplete(): Promise<boolean> {
		const result = await super.getUpdateComplete();
		await this.#applyFitOnceFontLoads();
		return result;
	}
}
if (!customElements.get(FIT_TEXT_TAG)) {
	customElements.define(FIT_TEXT_TAG, TktFitText);
}

declare global {
	interface HTMLElementTagNameMap {
		[FIT_TEXT_TAG]: TktFitText;
	}
}

/** One value slot, shrink-to-fit within `widthPx` — see this file's header
 *  for the algorithm and why the caller reserves a fixed two-line
 *  `min-height` around this regardless of content length. `overflow: hidden`
 *  is a backstop only, the same as `<tkt-fitted-wordmark>`'s own — the fit
 *  correction is what actually keeps content inside the box. */
export function renderFitValue(
	text: string,
	{ widthPx, fontSizePx }: { widthPx: number; fontSizePx: number },
): TemplateResult {
	return html`
    <div style="width: ${widthPx}px; overflow: hidden;">
      <tkt-fit-text text=${text} font-size-px=${fontSizePx}></tkt-fit-text>
    </div>
  `;
}
